import { Action } from '../actions';
import { MIN_ZOOM_LEVEL, NoteColor } from '../constants';
import { getProcessStepTranslations, DEFAULT_LOCALE, Locale } from "../services/translation-store";
import { SketchMode } from "./index";
import { ShapeData } from "../services/shape-service";


export interface PanningState {
    readonly offsetX: number,
    readonly offsetY: number,
}

export interface DraggingState {
    readonly uuid: string,
    readonly offsetX: number,
    readonly offsetY: number,
}

export interface ShapeDraggingState {
    readonly uuid: string,
    readonly offsetX: number,
    readonly offsetY: number,
    readonly boundsOffsetX: number,
    readonly boundsOffsetY: number,
}

export interface SelectorDraggingState {
    readonly shape: ShapeData,
    readonly offsetX: number,
    readonly offsetY: number,
    readonly xSelector: XSelector,
    readonly ySelector: YSelector,
}

export type SketchMode = 'NONE' | 'LINE' | 'RECT' | 'ELLIPSE' | 'ARROW' | 'FREEFORM' | 'TEXT';

export enum HelpPanelContentType { General, TemplateState }

export enum XSelector { x1, xm, x2 }
export enum YSelector { y1, ym, y2 }

export interface PaneState {
    readonly paneWidth: number,
    readonly paneHeight: number,
    readonly scrollLeft: number,
    readonly scrollTop: number,
    readonly zoomSliderValue: number,
    readonly maxZoomLevel: number,
}

export interface HelpPanelState {
    readonly visible: boolean,
    readonly contentType: HelpPanelContentType,
    readonly content: string,
    readonly backlink: string,
    readonly dragging: boolean,
    readonly width: number,
}

export interface GenericDialogState {
    readonly open: boolean,
    readonly title: string,
    readonly text: string,
}

export interface NoteDialogState {
    readonly open: boolean,
    readonly uuid: string,
    readonly text: string,
    readonly color: NoteColor
}

export interface SlideNotesDialogState {
    readonly open: boolean,
    readonly slideUuid: string,
    readonly text: string,
}

export interface TextShapeDialogState {
    readonly open: boolean,
    readonly slideUuid: string,
    readonly shapeUuid: string,
    readonly text: string,
}

export interface TimeDialogState {
    readonly open: boolean,
    readonly textFieldKey: string,
    readonly text: string,
}

export interface UIState {
    readonly error: Error | null,
    readonly locale: Locale,
    readonly paneState: PaneState,
    readonly panningState: PanningState | null,
    readonly noteDragging: DraggingState | null,
    readonly shapeDragging: ShapeDraggingState | null,
    readonly selectorDragging: SelectorDraggingState | null,
    readonly slideDragging: DraggingState | null,
    readonly sketchMode: SketchMode,
    readonly sketchState: DraggingState | null,
    readonly selectedShape: string | null,
    readonly helpPanel: HelpPanelState,
    readonly genericDialog: GenericDialogState,
    readonly noteDialog: NoteDialogState,
    readonly slideNotesDialog: SlideNotesDialogState,
    readonly textShapeDialog: TextShapeDialogState,
    readonly timeDialog: TimeDialogState,
}


const initialState: UIState = {
    error: null,
    locale: DEFAULT_LOCALE,
    paneState: {
        paneWidth: 1920,
        paneHeight: 1080,
        scrollLeft: 0,
        scrollTop: 0,
        zoomSliderValue: 0.0,
        maxZoomLevel: MIN_ZOOM_LEVEL,
    },
    panningState: null,
    noteDragging: null,
    shapeDragging: null,
    selectorDragging: null,
    slideDragging: null,
    sketchMode: 'NONE',
    sketchState: null,
    selectedShape: null,
    helpPanel: {
        visible: true,
        contentType: HelpPanelContentType.General,
        content: '',
        backlink: '',
        dragging: false,
        width:  0.2,
    },
    genericDialog: {
        open: false,
        title: '',
        text: '',
    },
    noteDialog: {
        open: false,
        uuid: "",
        text: '',
        color: NoteColor.YELLOW,
    },
    slideNotesDialog: {
        open: false,
        slideUuid: '',
        text: '',
    },
    textShapeDialog: {
        open: false,
        slideUuid: '',
        shapeUuid: '',
        text: '',
    },
    timeDialog: {
        open: false,
        textFieldKey: '',
        text: '',
    }
};


export default function handleAction(state: UIState = initialState, action: Action): UIState {
    switch (action.type) {

        case 'REPORT_ERROR':
            return {
                ...state,
                error: action.error,
            };

        case 'SET_PROCESS_STEP':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    zoomSliderValue: 0.0,
                },
                sketchMode: 'NONE',
                selectedShape: null,
                helpPanel: {
                    ...state.helpPanel,
                    visible: true,
                    contentType: HelpPanelContentType.General,
                    content: getProcessStepTranslations(action.newProcessStep).help_content,
                    backlink: '',
                }
            };

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    maxZoomLevel: action.newMaxZoomLevel,
                }
            };

        case 'SET_PRESENTATION':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    maxZoomLevel: action.maxZoomLevel,
                }
            };

        case 'SET_SKETCH_MODE':
            return {
                ...state,
                sketchMode: action.sketchMode,
                selectedShape: null,
            };

        case 'SET_LOCALE':
            return {
                ...state,
                locale: action.locale,
            };

        case 'SET_ZOOM_LEVEL':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    scrollLeft: action.scrollLeft,
                    scrollTop: action.scrollTop,
                    zoomSliderValue: action.zoomSliderValue,
                },
            };

        case 'SET_SCROLL':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    scrollLeft: action.scrollLeft,
                    scrollTop: action.scrollTop,
                },
            };

        case 'SET_PANE_SIZE':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    paneWidth: action.width,
                    paneHeight: action.height,
                },
            };

        case 'START_PANNING':
            return {
                ...state,
                panningState: {
                    offsetX: action.offsetX,
                    offsetY: action.offsetY,
                }
            };

        case 'PANNING':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    scrollLeft: action.newScrollLeft,
                    scrollTop: action.newScrollTop,
                },
            };

        case 'STOP_PANNING':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    scrollLeft: action.newScrollLeft,
                    scrollTop: action.newScrollTop,
                },
                panningState: null,
            };

        case 'START_HELP_PANEL_DRAGGING':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    dragging: true,
                }
            };

        case 'HELP_PANEL_DRAGGING':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    width: action.width,
                }
            };

        case 'STOP_HELP_PANEL_DRAGGING':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    dragging: false,
                    width: action.width,
                }
            };

        case 'START_NOTE_DRAGGING': {
            return {
                ...state,
                noteDragging: {
                    uuid: action.uuid,
                    offsetX: action.offsetX,
                    offsetY: action.offsetY,
                },
                selectedShape: null,
            };
        }

        case 'STOP_NOTE_DRAGGING':
            return {
                ...state,
                noteDragging: null,
            };

        case 'START_SLIDE_DRAGGING': {
            return {
                ...state,
                slideDragging: {
                    uuid: action.uuid,
                    offsetX: action.offsetX,
                    offsetY: action.offsetY,
                },
                selectedShape: null,
            };
        }

        case 'STOP_SLIDE_DRAGGING':
            return {
                ...state,
                slideDragging: null,
            };

        case 'START_SHAPE_DRAGGING': {
            return {
                ...state,
                shapeDragging: {
                    uuid: action.shapeUuid,
                    offsetX: action.offsetX,
                    offsetY: action.offsetY,
                    boundsOffsetX: action.boundsOffsetX,
                    boundsOffsetY: action.boundsOffsetY,
                },
                sketchMode: 'NONE',
                selectedShape: action.shapeUuid,
            };
        }

        case 'STOP_SHAPE_DRAGGING':
            return {
                ...state,
                shapeDragging: null,
            };

        case 'START_SELECTOR_DRAGGING': {
            return {
                ...state,
                selectorDragging: {
                    shape: action.shape,
                    offsetX: action.offsetX,
                    offsetY: action.offsetY,
                    xSelector: action.xSelector,
                    ySelector: action.ySelector,
                },
            };
        }

        case 'STOP_SELECTOR_DRAGGING':
            return {
                ...state,
                selectorDragging: null,
            };

        case 'START_SHAPE_SKETCHING': {
            return {
                ...state,
                sketchState: {
                    uuid: action.shape.uuid,
                    offsetX: -action.slideX,
                    offsetY: -action.slideY,
                },
                selectedShape: state.sketchMode === 'TEXT' ? action.shape.uuid : null,
            };
        }


        case 'STOP_SHAPE_SKETCHING':
            return {
                ...state,
                sketchMode: state.sketchMode === 'TEXT' ? 'NONE' : state.sketchMode,
                sketchState: null,
            };

        case 'DELETE_SHAPE':
            return {
                ...state,
                selectedShape: null,
            };


        case 'SELECT_SHAPE':
            return {
                ...state,
                sketchMode: 'NONE',
                selectedShape: action.shapeUuid,
            };


        case 'SHOW_UPDATE_NOTE_DIALOG':
            return {
                ...state,
                noteDialog: {
                    open: true,
                    uuid: action.uuid,
                    text: action.text,
                    color: action.color
                }
            };

        case 'SHOW_CREATE_NOTE_DIALOG':
            return {
                ...state,
                noteDialog: {
                    open: true,
                    uuid: "",
                    text: '',
                    color: action.color
                }
            };

        case 'CLOSE_NOTE_DIALOG':
            return {
                ...state,
                noteDialog: {
                    ...state.noteDialog,
                    open: false
                }
            };

        case 'TEXTAREA_CHANGED':
            return {
                ...state,
                noteDialog: {
                    ...state.noteDialog,
                    text: action.text
                }
            };

        case 'COLOR_CHANGED':
            return {
                ...state,
                noteDialog: {
                    ...state.noteDialog,
                    color: action.color
                }
            };

        case 'SHOW_HELP':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    visible: true,
                }
            };

        case 'HIDE_HELP':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    visible: false,
                }
            };

        case 'SHOW_HELP_CONTENT':
            return {
                ...state,
                helpPanel: {
                    ...state.helpPanel,
                    visible: true,
                    contentType: action.contentType,
                    content: action.content,
                    backlink: action.backlink,
                }
            };

        case 'SHOW_TIME_DIALOG':
            return {
                ...state,
                timeDialog: {
                    ...state.timeDialog,
                    open: true,
                    textFieldKey: action.textFieldKey,
                    text: action.text,
                }
            };

        case 'CLOSE_TIME_DIALOG':
            return {
                ...state,
                timeDialog: {
                    ...state.timeDialog,
                    open: false,
                }
            };

        case 'SHOW_GENERIC_DIALOG':
            return {
                ...state,
                genericDialog: {
                    ...state.genericDialog,
                    open: true,
                    title: action.title,
                    text: action.text,
                }
            };

        case 'HIDE_GENERIC_DIALOG':
            return {
                ...state,
                genericDialog: {
                    ...state.genericDialog,
                    open: false,
                }
            };

        case 'SHOW_SLIDE_NOTES_DIALOG':
            return {
                ...state,
                slideNotesDialog: {
                    ...state.slideNotesDialog,
                    open: true,
                    slideUuid: action.slideUuid,
                    text: action.text,
                }
            };

        case 'CLOSE_SLIDE_NOTES_DIALOG':
            return {
                ...state,
                slideNotesDialog: {
                    ...state.slideNotesDialog,
                    open: false,
                }
            };

        case 'SLIDE_NOTES_DIALOG_TEXT_CHANGED':
            return {
                ...state,
                slideNotesDialog: {
                    ...state.slideNotesDialog,
                    text: action.text,
                }
            };

        case 'SHOW_TEXT_SHAPE_DIALOG':
            return {
                ...state,
                textShapeDialog: {
                    ...state.textShapeDialog,
                    open: true,
                    slideUuid: action.slideUuid,
                    shapeUuid: action.shapeUuid,
                    text: action.text,
                }
            };

        case 'CLOSE_TEXT_SHAPE_DIALOG':
            return {
                ...state,
                textShapeDialog: {
                    ...state.textShapeDialog,
                    open: false,
                }
            };

        case 'TEXT_SHAPE_DIALOG_TEXT_CHANGED':
            return {
                ...state,
                textShapeDialog: {
                    ...state.textShapeDialog,
                    text: action.text,
                }
            };

        case 'TIME_DIALOG_TEXT_CHANGED':
            return {
                ...state,
                timeDialog: {
                    ...state.timeDialog,
                    text: action.text,
                }
            };

        case 'UNDO':
            return handleUndo(state, action.undoAction);

        case 'REDO':
            return handleAction(state, action.redoAction);


        default:
            return state;
    }
}

function handleUndo(state: UIState, action: Action): UIState {
    switch (action.type) {

        case 'SET_PROCESS_STEP':
            return {
                ...state,
                sketchMode: 'NONE',
                selectedShape: null,
                paneState: {
                    ...state.paneState,
                    zoomSliderValue: 0.0,
                },
                helpPanel: {
                    ...state.helpPanel,
                    visible: true,
                    contentType: HelpPanelContentType.General,
                    content: getProcessStepTranslations(action.oldProcessStep).help_content,
                }
            };

        case 'SET_TEMPLATE_ID':
            return {
                ...state,
                paneState: {
                    ...state.paneState,
                    maxZoomLevel: action.oldMaxZoomLevel,
                }
            };

        default:
            return state;
    }
}


export const getError = (state: UIState): Error | null => state.error;
export const getLocale = (state: UIState): Locale => state.locale;
export const getPaneState = (state: UIState): PaneState => state.paneState;
export const getZoomLevel = (state: UIState): number => {
    const paneState = getPaneState(state);
    return paneState.zoomSliderValue * paneState.zoomSliderValue * (paneState.maxZoomLevel - MIN_ZOOM_LEVEL) + MIN_ZOOM_LEVEL;
};
export const getPanning = (state: UIState): PanningState | null => state.panningState;
export const getNoteDragging = (state: UIState): DraggingState | null => state.noteDragging;
export const getShapeDragging = (state: UIState): ShapeDraggingState | null => state.shapeDragging;
export const getSelectorDragging = (state: UIState): SelectorDraggingState | null => state.selectorDragging;
export const getSlideDragging = (state: UIState): DraggingState | null => state.slideDragging;
export const getSketchMode = (state: UIState): SketchMode => state.sketchMode;
export const getSketchState = (state: UIState): DraggingState | null => state.sketchState;
export const getSelectedShape = (state: UIState): string | null => state.selectedShape;
export const getHelpPanelState = (state: UIState): HelpPanelState => state.helpPanel;
export const getGenericDialogState = (state: UIState): GenericDialogState => state.genericDialog;
export const getNoteDialogState = (state: UIState): NoteDialogState => state.noteDialog;
export const getSlideNotesDialogState = (state: UIState): SlideNotesDialogState => state.slideNotesDialog;
export const getTextShapeDialogState = (state: UIState): TextShapeDialogState => state.textShapeDialog;
export const getTimeDialogState = (state: UIState): TimeDialogState => state.timeDialog;



// WEBPACK FOOTER //
// ./src/reducers/ui-state.ts